import React from 'react';
import { useTranslation } from 'react-i18next';

const services = [
  { service: 'Building Permission (Up to 500 sq.m)', timeline: '21 Working Days', fee: 'As per Building Rules', officer: 'Town Planning Officer' },
  { service: 'Building Permission (Above 500 sq.m)', timeline: '30 Working Days', fee: 'As per Building Rules', officer: 'Asst. Director / Dy. Director' },
  { service: 'Building Permission (High Rise)', timeline: '45 Working Days', fee: 'As per Building Rules', officer: 'Director / Joint Director' },
  { service: 'Layout Approval (Up to 1 Acre)', timeline: '30 Working Days', fee: 'As per Layout Rules', officer: 'Town Planning Officer' },
  { service: 'Layout Approval (1-10 Acres)', timeline: '45 Working Days', fee: 'As per Layout Rules', officer: 'Asst. Director' },
  { service: 'Layout Approval (Above 10 Acres)', timeline: '60 Working Days', fee: 'As per Layout Rules', officer: 'Director' },
  { service: 'Occupancy Certificate', timeline: '15 Working Days', fee: 'As per Rules', officer: 'Town Planning Officer' },
  { service: 'Change of Land Use (CLU)', timeline: '45 Working Days', fee: 'As per GO', officer: 'Director' },
  { service: 'LRS Application', timeline: '30 Working Days', fee: 'As per LRS GO', officer: 'Asst. Director' },
  { service: 'BPS Application', timeline: '30 Working Days', fee: 'As per BPS GO', officer: 'Asst. Director' },
  { service: 'Master Plan Zoning Certificate', timeline: '7 Working Days', fee: 'Nominal', officer: 'Town Planning Officer' },
  { service: 'TDR Certificate', timeline: '30 Working Days', fee: 'As per Rules', officer: 'Director' },
];

export const CitizenCharterPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <a href="/about" className="hover:text-amber-700 transition-colors">{t('header.about')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.aboutCitizenCharter')}</span>
      </nav>

      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">Citizen Charter</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          DTCP is committed to providing transparent, time-bound, and efficient services to citizens. This charter outlines the services, timelines, and responsible officers for each service.
        </p>
      </div>

      {/* Commitment */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-10">
        <h2 className="text-lg font-bold text-amber-800 mb-3">Our Commitment</h2>
        <ul className="space-y-2 text-stone-700 text-sm">
          <li className="flex items-start space-x-2">
            <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <span>Process all applications within the stipulated timelines</span>
          </li>
          <li className="flex items-start space-x-2">
            <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <span>Provide transparent and accountable service delivery through online portals</span>
          </li>
          <li className="flex items-start space-x-2">
            <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <span>Ensure courteous and respectful treatment of all applicants</span>
          </li>
          <li className="flex items-start space-x-2">
            <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <span>Address grievances within 15 working days of receipt</span>
          </li>
          <li className="flex items-start space-x-2">
            <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <span>Maintain confidentiality of applicant information</span>
          </li>
        </ul>
      </div>

      {/* Services Table */}
      <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
        <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
        Services & Timelines
      </h2>
      <div className="overflow-x-auto border border-stone-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              <th className="text-left px-4 py-3 font-semibold text-stone-700">Service</th>
              <th className="text-left px-4 py-3 font-semibold text-stone-700">Timeline</th>
              <th className="text-left px-4 py-3 font-semibold text-stone-700">Fee</th>
              <th className="text-left px-4 py-3 font-semibold text-stone-700">Responsible Officer</th>
            </tr>
          </thead>
          <tbody>
            {services.map((svc, index) => (
              <tr key={index} className="border-b border-stone-100 hover:bg-amber-50/50 transition-colors">
                <td className="px-4 py-3 text-stone-800 font-medium">{svc.service}</td>
                <td className="px-4 py-3 text-stone-600">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">{svc.timeline}</span>
                </td>
                <td className="px-4 py-3 text-stone-600">{svc.fee}</td>
                <td className="px-4 py-3 text-stone-600">{svc.officer}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Grievance */}
      <div className="mt-10 bg-white border border-stone-200 rounded-lg p-6">
        <h2 className="text-lg font-bold text-stone-800 mb-3">Grievance Redressal</h2>
        <p className="text-sm text-stone-600 mb-4">
          If you are not satisfied with the service or the timeline is not met, you may escalate your grievance to:
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <i className="ri-user-line text-amber-600 mt-0.5"></i>
            <div>
              <div className="font-medium text-stone-800 text-sm">First Appellate Authority</div>
              <div className="text-xs text-stone-500">Joint Director, DTCP</div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <i className="ri-user-star-line text-amber-600 mt-0.5"></i>
            <div>
              <div className="font-medium text-stone-800 text-sm">Second Appellate Authority</div>
              <div className="text-xs text-stone-500">Director, DTCP</div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <i className="ri-phone-line text-amber-600 mt-0.5"></i>
            <div>
              <div className="font-medium text-stone-800 text-sm">Helpline</div>
              <div className="text-xs text-stone-500">0863-2000662</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
